import { useEffect, useMemo } from 'react';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { BlogBlock, getPostBySlug, getSortedPosts } from '../../data/blogPosts';
import { trackCta } from '../../lib/analytics';

const tagColor: Record<string, { bg: string; fg: string; bd: string }> = {
  Product: { bg: '#e6f0fa', fg: '#0b5394', bd: '#bcd5ee' },
  Coaching: { bg: '#fef7ec', fg: '#92400e', bd: '#fbd599' },
  Guides: { bg: '#e6f4ec', fg: '#0f5132', bd: '#bfe0cd' },
  'Behind the build': { bg: '#eef0f4', fg: '#334155', bd: '#cbd2dc' },
};

const calloutColor: Record<string, { bg: string; bd: string; fg: string }> = {
  info: { bg: '#eef4fb', bd: '#bcd5ee', fg: '#0b5394' },
  warn: { bg: '#fef7ec', bd: '#fbd599', fg: '#92400e' },
  success: { bg: '#e6f4ec', bd: '#bfe0cd', fg: '#0f5132' },
};

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function BlogPostPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const post = getPostBySlug(slug);

  const next = useMemo(() => {
    if (!post) return undefined;
    const all = getSortedPosts();
    const idx = all.findIndex((p) => p.slug === post.slug);
    if (idx === -1) return undefined;
    return all[idx + 1] ?? all[0 === idx ? 1 : 0];
  }, [post]);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} — Baton`;
    }
    window.scrollTo({ top: 0 });
  }, [post]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const colors = tagColor[post.tag] ?? tagColor['Product'];

  return (
    <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
      <Button
        component={RouterLink}
        to="/blog"
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ color: 'text.secondary', mb: 3, ml: -1 }}
        aria-label="Back to all posts"
      >
        All posts
      </Button>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ flexWrap: 'wrap', gap: 1 }}
      >
        <Chip
          size="small"
          label={post.tag}
          sx={{
            bgcolor: colors.bg,
            color: colors.fg,
            border: `1px solid ${colors.bd}`,
            fontWeight: 600,
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {formatDate(post.date)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ·
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {post.readMinutes} min read
        </Typography>
      </Stack>

      <Typography
        variant="h3"
        component="h1"
        sx={{
          mt: 2,
          fontWeight: 800,
          letterSpacing: '-0.02em',
          fontSize: { xs: 30, sm: 38, md: 44 },
          lineHeight: 1.15,
        }}
      >
        {post.title}
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 2, fontSize: 18, lineHeight: 1.55 }}>
        {post.excerpt}
      </Typography>

      <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
        By {post.author}
      </Typography>

      <Divider sx={{ my: 4 }} />

      <Box component="article">
        {post.body.map((block, i) => (
          <BlockRenderer key={i} block={block} />
        ))}
      </Box>

      <Divider sx={{ my: 6 }} />

      <Box
        sx={{
          p: { xs: 3, sm: 4 },
          textAlign: 'center',
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 3,
          bgcolor: 'background.paper',
        }}
      >
        <Typography sx={{ fontWeight: 700 }}>
          Want Baton for your team?
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: 14 }}>
          Founding coaches use Baton free for life.
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          justifyContent="center"
          sx={{ mt: 2 }}
        >
          <Button
            component={RouterLink}
            to="/#waitlist"
            variant="contained"
            aria-label="Get on the waitlist"
            onClick={() => trackCta('Waitlist', 'blog_post')}
          >
            Get on the waitlist
          </Button>
          <Button
            component={RouterLink}
            to="/tools"
            variant="outlined"
            aria-label="Explore the free tools"
            onClick={() => trackCta('Heat Seeder', 'blog_post')}
          >
            Try the free tools
          </Button>
        </Stack>
      </Box>

      {next && next.slug !== post.slug && (
        <Box sx={{ mt: 6 }}>
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1 }}
          >
            Read next
          </Typography>
          <Card variant="outlined" sx={{ mt: 1.5, borderColor: 'divider' }}>
            <CardActionArea
              component={RouterLink}
              to={`/blog/${next.slug}`}
              aria-label={`Read next: ${next.title}`}
            >
              <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <Typography
                  variant="h6"
                  component="p"
                  sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}
                >
                  {next.title}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  {next.excerpt}
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{ mt: 2 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: 'primary.main' }}
                  >
                    Continue reading
                  </Typography>
                  <ArrowForwardIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        </Box>
      )}
    </Container>
  );
}

function BlockRenderer({ block }: { block: BlogBlock }) {
  switch (block.type) {
    case 'p':
      return (
        <Typography
          sx={{ fontSize: 17, lineHeight: 1.7, color: 'text.primary', mb: 2.5 }}
        >
          {block.text}
        </Typography>
      );
    case 'h2':
      return (
        <Typography
          variant="h5"
          component="h2"
          sx={{
            mt: 4.5,
            mb: 2,
            fontWeight: 700,
            letterSpacing: '-0.01em',
          }}
        >
          {block.text}
        </Typography>
      );
    case 'h3':
      return (
        <Typography
          variant="h6"
          component="h3"
          sx={{ mt: 3.5, mb: 1.5, fontWeight: 700 }}
        >
          {block.text}
        </Typography>
      );
    case 'ul':
      return (
        <Box component="ul" sx={{ pl: 3, mb: 2.5 }}>
          {block.items.map((it, i) => (
            <Box
              component="li"
              key={i}
              sx={{ fontSize: 17, lineHeight: 1.7, mb: 0.75 }}
            >
              {it}
            </Box>
          ))}
        </Box>
      );
    case 'ol':
      return (
        <Box component="ol" sx={{ pl: 3, mb: 2.5 }}>
          {block.items.map((it, i) => (
            <Box
              component="li"
              key={i}
              sx={{ fontSize: 17, lineHeight: 1.7, mb: 0.75 }}
            >
              {it}
            </Box>
          ))}
        </Box>
      );
    case 'quote':
      return (
        <Box
          component="blockquote"
          sx={{
            m: 0,
            my: 3,
            pl: 2.5,
            borderLeft: '3px solid',
            borderColor: 'primary.light',
            color: 'text.secondary',
            fontStyle: 'italic',
          }}
        >
          <Typography sx={{ fontSize: 17, lineHeight: 1.7 }}>
            "{block.text}"
          </Typography>
          {block.cite && (
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              — {block.cite}
            </Typography>
          )}
        </Box>
      );
    case 'callout': {
      const tone = block.tone ?? 'info';
      const c = calloutColor[tone];
      return (
        <Box
          role="note"
          sx={{
            my: 3,
            p: 2.5,
            border: `1px solid ${c.bd}`,
            bgcolor: c.bg,
            borderRadius: 2,
          }}
        >
          {block.title && (
            <Typography
              sx={{ fontWeight: 700, color: c.fg, fontSize: 14, mb: 0.5 }}
            >
              {block.title}
            </Typography>
          )}
          <Typography sx={{ fontSize: 16, lineHeight: 1.6, color: 'text.primary' }}>
            {block.text}
          </Typography>
        </Box>
      );
    }
    case 'code':
      return (
        <Box
          component="pre"
          sx={{
            my: 2.5,
            p: 2,
            bgcolor: '#0f1828',
            color: '#e2e8f0',
            borderRadius: 2,
            overflow: 'auto',
            fontSize: 14,
            lineHeight: 1.55,
          }}
        >
          <code>{block.text}</code>
        </Box>
      );
    default:
      return null;
  }
}
